import { useEffect, useRef, useState } from "react";
import { SelectionRange } from "@progress/kendo-react-dateinputs";
import { X } from "lucide-react";
import { EmptyDataPlaceholder } from "@/components/src/empty-with-label/empty-data-placeholder.component";
import { formatLocalDate } from "@/lib/utils/format-local-date";
import { CustomDateRangePicker } from "../../../../components/custom-date-range-picker/custom-date-range-picker.component";
import { PaginationControls } from "../../../../components/ui/pagination-controls.component";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../../../components/ui/select";
import { useRemindersStore } from "../../../../store/Reminders";
import { ReminderHistoryQuery } from "../../../../store/Reminders/reminders.store";
import {
	MahnwesenFilters,
	MahnwesenView,
	ReminderHistorySortColumn,
	ReminderHistoryStatusFilter,
} from "../payment-reminder.types";
import { MahnwesenToolbar } from "./mahnwesen-toolbar.component";
import { ReminderHistoryTable } from "./reminder-history-table.component";

const ALL_STATUSES = "all";

const STATUS_FILTERS: { value: ReminderHistoryStatusFilter; label: string }[] =
	[
		{ value: "active", label: "Aktiv" },
		{ value: "open", label: "Offen" },
		{ value: "paid", label: "Gezahlt" },
		{ value: "canceled", label: "Storniert" },
	];

const INITIAL_QUERY: ReminderHistoryQuery = {
	search: "",
	stage: null,
	recipientId: null,
	dateFrom: null,
	dateTo: null,
	sortBy: "sent_at",
	sortDesc: true,
	status: null,
	pageIndex: 0,
	pageSize: 50,
};

interface RemindersHistoryTabProps {
	onViewChange: (view: MahnwesenView) => void;
	highlightedReminderIds: number[];
}

export const RemindersHistoryTab: React.FC<RemindersHistoryTabProps> = ({
	onViewChange,
	highlightedReminderIds,
}) => {
	const history = useRemindersStore((state) => state.history);
	const historyTotal = useRemindersStore((state) => state.historyTotal);
	const loading = useRemindersStore((state) => state.historyLoading);
	const fetchHistory = useRemindersStore((state) => state.fetchHistory);
	const [query, setQuery] = useState<ReminderHistoryQuery>(INITIAL_QUERY);
	const [dateRange, setDateRange] = useState<SelectionRange>({
		start: null,
		end: null,
	});
	const searchTimeout = useRef<ReturnType<typeof setTimeout>>();
	const pageCount = Math.max(1, Math.ceil(historyTotal / query.pageSize));
	const isFiltered =
		query.search.trim() !== "" ||
		query.stage !== null ||
		query.recipientId !== null ||
		query.dateFrom !== null ||
		query.status !== null;

	useEffect(() => {
		fetchHistory(INITIAL_QUERY);
	}, [fetchHistory]);

	const load = (changes: Partial<ReminderHistoryQuery>) => {
		const next = { ...query, ...changes };
		setQuery(next);
		clearTimeout(searchTimeout.current);
		fetchHistory(next);
	};

	const handleFiltersChange = (changes: Partial<MahnwesenFilters>) => {
		if (changes.search === undefined) {
			load({ ...changes, pageIndex: 0 });
			return;
		}
		const next = { ...query, ...changes, pageIndex: 0 };
		setQuery(next);
		clearTimeout(searchTimeout.current);
		searchTimeout.current = setTimeout(() => fetchHistory(next), 300);
	};

	const handleDateRangeChange = (range: SelectionRange) => {
		setDateRange(range);
		if (Boolean(range.start) !== Boolean(range.end)) return;
		load({
			dateFrom: range.start ? formatLocalDate(range.start) : null,
			dateTo: range.end ? formatLocalDate(range.end) : null,
			pageIndex: 0,
		});
	};

	const handleStatusChange = (value: string) =>
		load({
			status:
				STATUS_FILTERS.find((entry) => entry.value === value)?.value ??
				null,
			pageIndex: 0,
		});

	const handleSort = (column: ReminderHistorySortColumn) =>
		load({
			sortBy: column,
			sortDesc: query.sortBy === column ? !query.sortDesc : false,
			pageIndex: 0,
		});

	return (
		<>
			<MahnwesenToolbar
				view="history"
				onViewChange={onViewChange}
				filters={query}
				onFiltersChange={handleFiltersChange}
			>
				<div className="flex items-center gap-2">
					<span className="whitespace-nowrap text-sm text-gray-500">
						Status:
					</span>
					<Select
						value={query.status ?? ALL_STATUSES}
						onValueChange={handleStatusChange}
					>
						<SelectTrigger
							className="h-10 w-36"
							aria-label="Status filtern"
							data-testid="reminder-history-status-filter"
						>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value={ALL_STATUSES}>Alle</SelectItem>
							{STATUS_FILTERS.map((entry) => (
								<SelectItem
									key={entry.value}
									value={entry.value}
								>
									{entry.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="flex items-center gap-2">
					<span className="whitespace-nowrap text-sm text-gray-500">
						Zeitraum:
					</span>
					<CustomDateRangePicker
						dateVal={dateRange}
						handleDateChange={handleDateRangeChange}
					/>
					{(dateRange.start || dateRange.end) && (
						<button
							type="button"
							onClick={() =>
								handleDateRangeChange({
									start: null,
									end: null,
								})
							}
							className="text-gray-400 hover:text-gray-600"
							aria-label="Zeitraum löschen"
							data-testid="reminder-history-clear-date-range"
						>
							<X size={16} />
						</button>
					)}
				</div>
			</MahnwesenToolbar>

			{loading && history.length === 0 ? (
				<div className="flex items-center justify-center py-8 text-sm text-gray-500">
					Lade Verlauf…
				</div>
			) : history.length === 0 ? (
				<EmptyDataPlaceholder
					label={
						isFiltered
							? "Keine Mahnungen für diese Filter"
							: "Noch keine Mahnungen"
					}
				/>
			) : (
				<>
					<ReminderHistoryTable
						rows={history}
						onChanged={() => fetchHistory(query)}
						sorting={query}
						onSort={handleSort}
						highlightedReminderIds={highlightedReminderIds}
					/>
					<div className="mt-4">
						<PaginationControls
							pageIndex={query.pageIndex}
							pageSize={query.pageSize}
							totalItems={historyTotal}
							pageCount={pageCount}
							canPreviousPage={query.pageIndex > 0}
							canNextPage={query.pageIndex < pageCount - 1}
							onPageSizeChange={(pageSize) =>
								load({ pageSize, pageIndex: 0 })
							}
							onPageIndexChange={(pageIndex) =>
								load({ pageIndex })
							}
							onPreviousPage={() =>
								load({ pageIndex: query.pageIndex - 1 })
							}
							onNextPage={() =>
								load({ pageIndex: query.pageIndex + 1 })
							}
						/>
					</div>
				</>
			)}
		</>
	);
};
